import type { PropsWithChildren } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Marquee } from '#src/atoms/container/Marquee/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, BrandM } from '#src/atoms/typography/index.tsx';
import { Img } from '#src/atoms/ui/Img/index.tsx';
import { NewSeasonLogo } from '#src/components/Season2026/NewSeasonLogo/index.tsx';
import type { League } from '#src/components/landing/types.ts';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { glossary } from '#src/lib/glossary.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

import defaultBackground from './assets/background.svg';

const Wrapper = styled(Vertical).attrs({
  gap: 4,
})`
  margin-bottom: calc(var(--unit) * 12);
  padding-top: calc(var(--unit) * 6);
  background:
    radial-gradient(
      30% 30% at 50% 50%,
      rgba(109, 88, 33, 0.8) 0%,
      rgba(109, 88, 33, 0.4) 60%,
      transparent 100%
    ),
    linear-gradient(to bottom, transparent 0%, #000 100%),
    url(${defaultBackground});
  img {
    width: 100%;
  }

  @media ${tabletAndAbove} {
    gap: var(--triple-unit);
  }
`;

const TextContainer = styled(Vertical)`
  justify-content: center;
  text-align: center;
`;

const TitleText = styled.h2`
  font-size: var(--fs-landing-title);
  line-height: 1;
  font-family: var(--brand-font);
  text-transform: uppercase;
`;

const MarqueeInnerContainer = styled(Horizontal).attrs({
  center: true,
})`
  background-color: #0026ff;
  color: #00158f;
  padding: var(--intermediate-unit);
  padding-right: 0;
  width: 100%;
`;

const MarqueeContainer = styled.div`
  transform: rotate(-1.5deg);
  min-width: var(--100vw);
`;

const RewardsContainer = styled.div`
  display: grid;
  height: 200px;
  grid-auto-columns: 1fr;
  grid-template-columns: repeat(8, 109px);
  grid-template-rows: repeat(2, 100px);
  gap: 0;
  @media ${tabletAndAbove} {
    height: 400px;
    grid-template-columns: repeat(8, 218px);
    grid-template-rows: repeat(2, 200px);
  }
`;

const Reward = styled(Img)`
  align-self: center;
  justify-self: center;
  background-image: var(--reward);
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  max-height: 100%;
  max-width: max-content;
  &:hover {
    transition: transform 0.3s ease-in-out;
    transform: scale(1.05);
  }
`;

type RewardMarqueeAsset = {
  name: string;
  src: string;
  width: number;
  height: number;
  gridArea: string;
};

type Props = {
  league: League;
  assets?: RewardMarqueeAsset[];
  messages: {
    win: MessageDescriptor;
    winDescription: MessageDescriptor;
    marquee: MessageDescriptor;
  };
  background?: string;
};

export const Win = ({
  league,
  assets,
  messages,
  background,
  children,
}: PropsWithChildren<Props>) => {
  const { signUp } = useConnectionContext();
  const track = useEvents();

  const onClick = () => {
    track('Click Landing Page Win', { clickSource: league });
    signUp();
  };
  return (
    <Wrapper
      style={{
        background: background
          ? `linear-gradient(to top, var(--c-black), transparent 10%), ${background}`
          : undefined,
        backgroundSize: background ? 'cover' : undefined,
        backgroundBlendMode: background
          ? undefined
          : 'lighten, lighten, normal',
      }}
    >
      <Marquee>
        {!!assets && (
          <RewardsContainer>
            {assets.map(asset => (
              <Reward
                key={asset.name}
                src={asset.src}
                width={asset.width}
                height={asset.height}
                style={{ gridArea: asset.gridArea }}
              />
            ))}
          </RewardsContainer>
        )}
        {children}
      </Marquee>
      <TextContainer center gap={4}>
        <TitleText>
          <FormattedMessage {...messages.win} />
        </TitleText>
        <BodyM text-center>
          <FormattedMessage {...messages.winDescription} />
        </BodyM>
        <Button color="secondary" size="large" onClick={onClick}>
          <FormattedMessage {...glossary.playNow} />
        </Button>
      </TextContainer>
      <MarqueeContainer>
        <Marquee gap={0} stretch>
          <MarqueeInnerContainer gap={1}>
            <BrandM>
              <FormattedMessage {...messages.marquee} />
            </BrandM>
            <NewSeasonLogo
              size="small"
              iconSize={4}
              gap="small"
              color="#00158F"
            />
          </MarqueeInnerContainer>
        </Marquee>
      </MarqueeContainer>
    </Wrapper>
  );
};
